import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';

export default function FAQs() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      question: '¿Qué servicios ofrece Suvi Digital?',
      answer: 'Ofrecemos estrategia digital, branding e identidad visual, e-commerce, gestión de redes sociales, publicidad digital (Meta Ads, Google Ads), SEO, analítica web y software especializado como Operturi. Todo enfocado en agencias de viaje y tour operadores.',
    },
    {
      question: '¿Trabajan solo con agencias de viaje?',
      answer: 'Sí, estamos especializados exclusivamente en el sector turístico. Este enfoque nos permite entender a fondo los desafíos, estacionalidad, dinámicas de precios y comportamiento del viajero moderno.',
    },
    {
      question: '¿En qué países operan?',
      answer: 'Trabajamos de forma remota con clientes en Perú, Chile, Colombia, México, España y otros países de habla hispana. Nuestro equipo está basado en Lima, Perú.',
    },
    {
      question: '¿Cuánto cuesta trabajar con Suvi Digital?',
      answer: 'Ofrecemos planes desde consultoría puntual hasta gestión integral mensual. La inversión depende del alcance, canales y objetivos. Agenda una asesoría gratuita y te enviamos una propuesta personalizada.',
    },
    {
      question: '¿Cuánto tiempo tarda en verse resultados?',
      answer: 'En publicidad digital puedes ver resultados en 2-4 semanas. SEO y posicionamiento orgánico toman 3-6 meses. Branding y e-commerce dependen del alcance del proyecto. Siempre establecemos KPIs claros desde el inicio.',
    },
    {
      question: '¿Qué es Operturi?',
      answer: 'Operturi es nuestro software propietario diseñado para agencias de viaje y tour operadores. Permite gestionar cotizaciones, itinerarios, reservas, clientes y enviar propuestas interactivas. Reduce el tiempo administrativo hasta en un 60%.',
    },
    {
      question: '¿Ofrecen garantía de resultados?',
      answer: 'No prometemos resultados mágicos, pero sí establecemos objetivos medibles y reportamos avances mensualmente. Nuestro 98% de clientes satisfechos respalda nuestra metodología.',
    },
    {
      question: '¿Cómo empiezo a trabajar con ustedes?',
      answer: 'El primer paso es una asesoría gratuita de 30 minutos. Analizamos tu situación actual, objetivos y te presentamos una propuesta personalizada. Puedes agendar por el formulario de contacto o WhatsApp.',
    },
    {
      question: '¿Pueden trabajar con mi agencia si ya tengo un sitio web?',
      answer: 'Absolutamente. Muchos de nuestros clientes ya cuentan con presencia digital. Podemos optimizar lo existente, crear nuevas landing pages, mejorar el SEO o implementar campañas de tráfico sin necesidad de reconstruir todo desde cero.',
    },
    {
      question: '¿Cómo se maneja la comunicación durante el proyecto?',
      answer: 'Asignamos un project manager como punto de contacto. Trabajamos con reuniones semanales o quincenales, reportes mensuales de métricas, y comunicación fluida por WhatsApp y email. Usamos herramientas como Notion y Google Workspace.',
    },
  ];

  return (
    <>
      <SEOHead
        title="Preguntas Frecuentes | Suvi Digital"
        description="Respuestas a las preguntas más comunes sobre nuestros servicios de marketing digital para agencias de viaje, precios, metodología y más."
        keywords="preguntas frecuentes marketing digital, FAQ agencia viaje, costo marketing digital turismo, Suvi Digital preguntas"
        canonical="https://suvidigital.com/faqs"
        schemaType="FAQPage"
      />
      <main className="min-h-screen">
        {/* Hero */}
        <section className="relative min-h-[380px] md:min-h-[450px] flex items-center overflow-hidden">
          <div className="absolute inset-0 animate-gradient bg-gradient-to-br from-primary-100 via-background-50 to-secondary-100" />
          <div className="absolute top-20 right-20 w-72 h-72 bg-primary-200/30 rounded-full blur-3xl animate-float-slow" />
          <div className="absolute bottom-20 left-10 w-64 h-64 bg-accent-200/20 rounded-full blur-3xl animate-float-delay-1" />

          <div className="relative z-10 w-full px-4 md:px-6 lg:px-10 xl:px-16 pt-24 md:pt-28">
            <div className="max-w-4xl">
              <span className="inline-block bg-primary-50 text-primary-600 text-sm font-semibold px-4 py-1.5 rounded-full mb-5 border border-primary-200/50">
                FAQ
              </span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-foreground-900 mb-5 leading-tight">
                Preguntas{' '}
                <span className="text-gradient">frecuentes</span>
              </h1>
              <p className="text-foreground-500 text-lg md:text-xl leading-relaxed max-w-2xl">
                Todo lo que necesitas saber sobre nuestros servicios, metodología y cómo trabajamos.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Accordion */}
        <section className="bg-white py-20 md:py-28">
          <div className="px-4 md:px-6 lg:px-10 xl:px-16">
            <div className="max-w-3xl mx-auto space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-background-50 border border-background-200/60 rounded-2xl overflow-hidden"
                >
                  <button
                    onClick={() => setOpenIndex(openIndex === index ? null : index)}
                    className="w-full flex items-center justify-between p-5 md:p-6 text-left group"
                  >
                    <span className="font-heading font-semibold text-base md:text-lg text-foreground-900 pr-4 group-hover:text-primary-700 transition-colors">
                      {faq.question}
                    </span>
                    <div className={`w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0 transition-all ${openIndex === index ? 'rotate-180 bg-primary-500 text-white' : ''}`}>
                      <i className="ri-arrow-down-s-line text-lg" />
                    </div>
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ${openIndex === index ? 'max-h-96' : 'max-h-0'}`}
                  >
                    <div className="px-5 md:px-6 pb-5 md:pb-6 pt-0">
                      <p className="text-foreground-500 text-sm md:text-base leading-relaxed border-t border-background-200/50 pt-4">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Still have questions */}
            <div className="text-center mt-14">
              <p className="text-foreground-500 text-base mb-4">¿No encuentras tu pregunta?</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contacto"
                  className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white font-semibold px-7 py-3.5 rounded-xl transition-all whitespace-nowrap card-lift"
                >
                  Contáctanos <i className="ri-arrow-right-line" />
                </Link>
                <a
                  href="https://wa.me/51989307864"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-white font-semibold px-7 py-3.5 rounded-xl transition-all whitespace-nowrap card-lift"
                >
                  <i className="ri-whatsapp-line" /> WhatsApp
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}